/* ═══════════════════════════════════════════════════════════
   tessara-product.js — PDP interactions.
   - Product type tab switching via Section Rendering API
   - Gallery: main image + thumbnails + pattern tile toggle
   - Size chip selection + variant ID sync
   - Tab overflow ("More" popover)
   ═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  const data = window.tessaraProduct;
  if (!data) return;

  /* ── Variant state ── */
  let activeVariantId = data.variants?.[0]?.id;

  // Tracks the active thumbnail index so pattern switches can restore gallery position
  let currentGalleryIndex = 0;

  // Map variant ID → featured image src (for mockup swap on size change)
  const variantImageMap = {};
  (data.variants || []).forEach((v) => {
    if (v.featured_image?.src) variantImageMap[v.id] = v.featured_image.src;
  });

  /* ════════════════════════════════════
     Gallery helpers
  ════════════════════════════════════ */

  // Strip Shopify CDN width param so the browser fetches the full-res image.
  function origUrl(src) {
    try {
      const u = new URL(src, window.location.href);
      u.searchParams.delete('width');
      return u.toString();
    } catch {
      return src;
    }
  }

  // Set the main gallery image. Clears srcset first so the browser doesn't
  // ignore the new src in favour of the Liquid-generated responsive srcset.
  function setMainImage(src) {
    const mainTile = document.getElementById('tds-main-img');
    if (!mainTile || !src) return;
    mainTile.removeAttribute('srcset');
    mainTile.removeAttribute('sizes');
    mainTile.src = origUrl(src);
  }

  // Return just the CDN pathname for comparison — strips protocol, host, and all
  // query params so that featured_image.src ("//tessara.ca/cdn/.../x.jpg?v=123")
  // matches data-thumb-src ("https://tessara.ca/cdn/.../x.jpg?width=600&format=webp").
  function imgPath(src) {
    try { return new URL(src, window.location.href).pathname; }
    catch { return src; }
  }

  // Sync thumbnail active state to whichever src matches current main image.
  function syncThumbActive(src) {
    const normSrc = imgPath(src);
    document.querySelectorAll('.tds-gallery__thumb').forEach((t) => {
      const isActive = imgPath(t.dataset.thumbSrc) === normSrc;
      t.classList.toggle('tds-gallery__thumb--active', isActive);
      t.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  /* ════════════════════════════════════
     Size chips
  ════════════════════════════════════ */
  function bindSizeChips() {
    const chips = document.querySelectorAll('.tds-size-chip:not(.tds-size-chip--oos)');
    const priceEl = document.querySelector('.tds-pdp-info__meta');

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        chips.forEach((c) => { c.classList.remove('tds-size-chip--active'); c.setAttribute('aria-pressed', 'false'); });
        chip.classList.add('tds-size-chip--active');
        chip.setAttribute('aria-pressed', 'true');

        const variantId = parseInt(chip.dataset.variantId);
        // Query fresh each click — closure capture breaks after outerHTML swaps
        const liveInput = document.getElementById('tds-variant-id');
        if (liveInput) liveInput.value = variantId;
        activeVariantId = variantId;

        // Update displayed price from the chip's data-price attribute (pre-formatted by Liquid)
        if (priceEl && chip.dataset.price) {
          priceEl.textContent = chip.dataset.price;
        }

        // Swap main image to the variant's mockup if one exists
        const imgSrc = variantImageMap[variantId];
        if (imgSrc) {
          setMainImage(imgSrc);
          syncThumbActive(imgSrc);
        }
      });
    });
  }

  /* ════════════════════════════════════
     Gallery
  ════════════════════════════════════ */
  function bindGallery() {
    // Suppress right-click context menu across the entire gallery area
    const galleryEl = document.getElementById('tds-gallery-main');
    if (galleryEl) {
      galleryEl.addEventListener('contextmenu', (e) => e.preventDefault());
    }
    document.querySelectorAll('.tds-gallery__thumb').forEach((t) => {
      t.addEventListener('contextmenu', (e) => e.preventDefault());
    });

    const thumbs = document.querySelectorAll('.tds-gallery__thumb');

    // Thumbnail clicks — clear srcset so browser uses the explicit src
    thumbs.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => {
        currentGalleryIndex = idx;
        const src = thumb.dataset.thumbSrc;
        setMainImage(src);
        thumbs.forEach((t) => {
          t.classList.remove('tds-gallery__thumb--active');
          t.setAttribute('aria-pressed', 'false');
        });
        thumb.classList.add('tds-gallery__thumb--active');
        thumb.setAttribute('aria-pressed', 'true');
      });
    });

    // Preload full-res versions so thumb clicks are instant
    thumbs.forEach((thumb) => {
      const img = new Image();
      img.src = origUrl(thumb.dataset.thumbSrc);
    });
  }

  /* ════════════════════════════════════
     Product type tab switching
  ════════════════════════════════════ */
  function bindTypeTabs() {
    const tabList = document.getElementById('tds-type-tabs');
    if (!tabList) return;

    const tabs = tabList.querySelectorAll('[role="tab"]');

    tabs.forEach((tab) => {
      tab.addEventListener('click', async () => {
        const handle = tab.dataset.productHandle;
        if (!handle || handle === data.handle) return;

        try {
          const res = await fetch(`/products/${handle}?sections=main-product`);
          if (!res.ok) throw new Error('section-render-fail');
          const json = await res.json();
          const html = json['main-product'];

          const target = document.getElementById('main-product');
          if (target && html) {
            target.outerHTML = html;
            history.pushState({}, '', `/products/${handle}`);

            // Re-init after swap — skip applyPreselectType to prevent re-trigger loop
            document.dispatchEvent(new Event('tessara:section-rendered'));
            initProduct(true);
          }
        } catch {
          const errDiv = document.createElement('div');
          errDiv.className = 'tds-inline-msg tds-inline-msg--error';
          errDiv.textContent = "Couldn't load this product. Try refreshing.";
          tabList.insertAdjacentElement('afterend', errDiv);
        }
      });
    });

    // Keyboard: arrow keys within tablist
    tabList.addEventListener('keydown', (e) => {
      const all = Array.from(tabList.querySelectorAll('[role="tab"]'));
      const idx = all.indexOf(document.activeElement);
      if (e.key === 'ArrowRight' && idx < all.length - 1) { all[idx + 1].focus(); }
      if (e.key === 'ArrowLeft'  && idx > 0)              { all[idx - 1].focus(); }
    });

    // Check for overflow
    requestAnimationFrame(() => checkTabOverflow(tabList));
  }

  function checkTabOverflow(tabList) {
    if (tabList.scrollWidth <= tabList.clientWidth) return;
    const tabs = Array.from(tabList.querySelectorAll('[role="tab"]'));
    const overflowIdx = findOverflowIndex(tabList, tabs);
    if (overflowIdx < 0) return;

    // Hide tabs beyond overflow index
    tabs.slice(overflowIdx).forEach((t) => { t.hidden = true; });

    // Add "More" button
    const more = document.createElement('button');
    more.className = 'tds-product-type-tabs__more';
    more.textContent = `More (${tabs.length - overflowIdx})`;
    more.addEventListener('click', () => {
      tabs.forEach((t) => { t.hidden = false; });
      more.remove();
      tabList.style.flexWrap = 'wrap';
    });
    tabList.appendChild(more);
  }

  function findOverflowIndex(tabList, tabs) {
    const maxWidth = tabList.clientWidth;
    let total = 0;
    for (let i = 0; i < tabs.length; i++) {
      total += tabs[i].offsetWidth + 8; // 8 = gap
      if (total > maxWidth - 80) return i; // reserve 80px for "More" btn
    }
    return -1;
  }

  /* ════════════════════════════════════
     URL ?type= pre-selection
  ════════════════════════════════════ */
  function applyPreselectType() {
    const preselect = data.preselectType;
    if (!preselect) return;
    const tabs = document.querySelectorAll('#tds-type-tabs [role="tab"]');
    tabs.forEach((tab) => {
      const inner = tab.querySelector('span')?.textContent?.toLowerCase().replace(/\s/g, '-');
      if (inner && preselect.toLowerCase().includes(inner)) {
        tab.click();
      }
    });
  }

  /* ════════════════════════════════════
     Siblings "More" chip
  ════════════════════════════════════ */
  function bindSiblingsMore() {
    const moreBtn = document.getElementById('tds-siblings-more');
    if (!moreBtn) return;

    const useDrawer = moreBtn.dataset.useDrawer === 'true';

    moreBtn.addEventListener('click', () => {
      if (useDrawer) {
        // Grid drawer for > 100 siblings — simplified inline expand
        const container = moreBtn.closest('.tds-pattern-siblings');
        if (container) container.style.flexWrap = 'wrap';
        moreBtn.remove();
      } else {
        // Inline expand
        const container = moreBtn.closest('.tds-pattern-siblings');
        if (container) container.style.flexWrap = 'wrap';
        document.querySelectorAll('.tds-sibling-swatch[hidden]').forEach((s) => { s.hidden = false; });
        moreBtn.remove();
      }
    });
  }

  /* ════════════════════════════════════
     PDP qty stepper
  ════════════════════════════════════ */
  function bindQtyStepper() {
    const dec   = document.getElementById('tds-qty-dec');
    const inc   = document.getElementById('tds-qty-inc');
    const input = document.getElementById('tds-qty-input');
    if (!dec || !inc || !input) return;

    dec.addEventListener('click', () => {
      const val = parseInt(input.value, 10) || 1;
      if (val > 1) input.value = val - 1;
    });
    inc.addEventListener('click', () => {
      const val = parseInt(input.value, 10) || 1;
      input.value = val + 1;
    });
    input.addEventListener('change', () => {
      const val = parseInt(input.value, 10);
      input.value = isNaN(val) || val < 1 ? 1 : val;
    });
  }

  /* ════════════════════════════════════
     Pattern sibling switching (no page reload)
  ════════════════════════════════════ */
  function bindPatternSiblings() {
    document.querySelectorAll('.tds-sibling-swatch:not(.tds-sibling-swatch--active)').forEach((swatch) => {
      swatch.addEventListener('click', async (e) => {
        e.preventDefault();
        const handle = swatch.pathname.split('/products/').pop();
        const savedIndex = currentGalleryIndex;

        try {
          const res = await fetch(`/products/${handle}?sections=main-product`);
          if (!res.ok) throw new Error('section-render-fail');
          const json = await res.json();
          const html = json['main-product'];

          const target = document.getElementById('main-product');
          if (target && html) {
            target.outerHTML = html;
            history.pushState({}, '', swatch.href);

            document.dispatchEvent(new Event('tessara:section-rendered'));
            initProduct(true);

            // Restore the same gallery position after the new section renders
            requestAnimationFrame(() => {
              const newThumbs = Array.from(document.querySelectorAll('.tds-gallery__thumb'));
              const thumb = newThumbs[savedIndex] || newThumbs[0];
              if (thumb) thumb.click();
            });
          }
        } catch {
          window.location.href = swatch.href;
        }
      });
    });
  }

  /* ── Init ── */
  function initProduct(isReInit = false) {
    bindSizeChips();
    bindGallery();
    bindTypeTabs();
    bindPatternSiblings();
    bindSiblingsMore();
    bindQtyStepper();
    if (!isReInit) applyPreselectType();
  }

  document.addEventListener('DOMContentLoaded', () => initProduct(false));

})();
